"use client";

import * as React from "react";
import { format } from "date-fns";
import { TZDate } from "@date-fns/tz";
import { formatInTimeZone } from "date-fns-tz";
import type { Matcher } from "react-day-picker";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "../../lib/utils";
import { formatTimezoneLabel, getLocalTimezone } from "../../lib/timezone";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { DatePickerCalendar, toZonedMatchers } from "./date-picker-calendar";
import {
	getDateWithDefaultTime,
	TimePicker12Hours,
	type TimePickerHandle,
	type TimePickerDefaultTime,
} from "./time-picker";

const DATE_FORMAT = "d MMM yyyy";
const DATE_TIME_FORMAT = "d MMM yyyy h:mm a";

type DateTimePickerProps = {
	id?: string;
	date?: Date;
	onDateChange?: (date: Date | undefined) => void;
	onBlur?: (e: React.FocusEvent<HTMLButtonElement>) => void;
	className?: string;
	maxDate?: Date;
	minDate?: Date;
	/** Specific dates to disable */
	disabledDates?: Date[];
	placeholder?: string;
	isInvalid?: boolean;
	disabled?: boolean;
	showTimeSelect?: boolean;
	/** Default time to set when a date is selected. Only applies if there is no existing value */
	defaultTime?: TimePickerDefaultTime;
	/** The initial month to show in the calendar. Defaults to current month */
	defaultMonth?: Date;
	disabledDays?: Matcher | Matcher[];
	/** Whether to show the clear button when a date is selected. Defaults to true. */
	clearable?: boolean;
	/**
	 * When set, the picker interprets and displays the value in this IANA timezone (e.g.
	 * `"Pacific/Auckland"`) instead of the browser's. `date` / `onDateChange` stay absolute instants;
	 * only the displayed day/time, the calendar selection, and the date constraints (`minDate` /
	 * `maxDate` / `disabledDates` / `defaultMonth`) are resolved in this zone. Leave undefined for
	 * browser-timezone behaviour.
	 */
	timezone?: string;
	/** Optional small helper text rendered beneath the picker (e.g. naming the recorded timezone). */
	hint?: string;
};

export function DateTimePicker({
	id,
	date: controlledDate,
	onDateChange,
	onBlur,
	className,
	maxDate,
	minDate,
	disabledDates,
	placeholder,
	showTimeSelect = false,
	defaultTime = "startOfDay",
	isInvalid,
	disabled,
	defaultMonth,
	disabledDays,
	clearable = true,
	timezone,
	hint,
}: DateTimePickerProps) {
	const [internalDate, setInternalDate] = React.useState<Date | undefined>(
		controlledDate,
	);
	const [open, setOpen] = React.useState(false);
	const [calendarViewMode, setCalendarViewMode] = React.useState<
		"calendar" | "month" | "year"
	>("calendar");

	const date = controlledDate ?? internalDate;

	const timeInputRef = React.useRef<TimePickerHandle>(null);

	// When a timezone is set, the calendar resolves in-zone via react-day-picker's native `timeZone`
	// and the time picker operates on a `TZDate`, so the whole picker (day + time) runs in that zone.
	// `date` / `onDateChange` stay absolute instants; the zone only affects display and selection.
	const toZoned = (value: Date | undefined) =>
		timezone && value ? new TZDate(value.getTime(), timezone) : value;

	const zonedDate = toZoned(date);
	const zonedDisabledDates =
		timezone && disabledDates
			? disabledDates.map((d) => new TZDate(d.getTime(), timezone))
			: disabledDates;
	const zonedDisabledDays = toZonedMatchers(disabledDays, timezone);

	// Always surface the zone the popover is operating in (the explicit `timezone` or the viewer's
	// local zone), resolved against the selected instant so DST is reflected.
	const timezoneLabel = formatTimezoneLabel(
		timezone ?? getLocalTimezone(),
		date,
	);

	const handleDateChange = (newDate: Date | undefined) => {
		if (!newDate) {
			setInternalDate(undefined);
			onDateChange?.(undefined);
			return;
		}
		// `newDate` is a zoned `TZDate` (from the calendar or time picker); collapse it back to a plain
		// absolute instant so consumers never receive a zone-tagged Date.
		const resolved = date
			? newDate
			: getDateWithDefaultTime(newDate, defaultTime);
		const instant = new Date(resolved.getTime());
		setInternalDate(instant);
		onDateChange?.(instant);
	};

	const formatDate = (d: Date) => {
		const dateFormat = showTimeSelect ? DATE_TIME_FORMAT : DATE_FORMAT;
		return timezone
			? formatInTimeZone(d, timezone, dateFormat)
			: format(d, dateFormat);
	};

	return (
		<>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						id={id}
						variant="outline"
						className={cn(
							"w-full h-8 rounded group pl-3 pr-2 hover:bg-neutral-50",
							"text-left text-xs text-neutral-600 hover:text-neutral-600",
							isInvalid && "outline-danger outline-1 outline",
							className,
						)}
						onBlur={onBlur}
						disabled={disabled}
					>
						<CalendarIcon className="mr-1 h-4 w-4 text-neutral-400" />
						<span className="flex-1 truncate">
							{date ? formatDate(date) : placeholder}
						</span>
						{date && clearable && (
							<div
								role="button"
								aria-label="Clear date"
								tabIndex={-1}
								className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
								onClick={(e) => {
									e.stopPropagation();
									handleDateChange(undefined);
								}}
								onKeyDown={(e) => {
									if (e.key !== "Enter" && e.key !== " ") {
										return;
									}
									e.preventDefault();
									e.stopPropagation();
									handleDateChange(undefined);
								}}
							>
								<X className="h-2 w-2 text-neutral-500" />
							</div>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0" align="start">
					<div data-testid="date-time-picker-popover">
						<DatePickerCalendar
							mode="single"
							date={zonedDate}
							onDayClick={() => {
								if (!showTimeSelect) {
									setOpen(false);
								} else {
									timeInputRef?.current?.focus();
								}
							}}
							onDateChange={handleDateChange}
							maxDate={toZoned(maxDate)}
							minDate={toZoned(minDate)}
							disabledDates={zonedDisabledDates}
							onViewModeChange={setCalendarViewMode}
							defaultMonth={toZoned(defaultMonth)}
							disabledDays={zonedDisabledDays}
							timeZone={timezone}
						/>

						{calendarViewMode === "calendar" && showTimeSelect && (
							<div className="border-t border-neutral-200 flex justify-center py-2">
								<TimePicker12Hours
									setDate={handleDateChange}
									date={zonedDate}
									defaultTime={defaultTime}
									ref={timeInputRef}
								/>
							</div>
						)}
						<div
							className={cn(
								"text-center text-xs text-neutral-500 py-2 px-8 border-t border-neutral-200",
								showTimeSelect && "border-t-0 pt-0",
								calendarViewMode !== "calendar" && "hidden",
							)}
						>
							{timezoneLabel}
						</div>
					</div>
				</PopoverContent>
			</Popover>
			{hint && <p className="mt-1 text-xs text-neutral-500">{hint}</p>}
		</>
	);
}
